import { DARK_CLASS, THEME_STORAGE_KEY, preferredTheme } from '../core/theme/preferred-theme';
import type { Theme } from '../core/theme/theme';

/**
 * Reading and writing the appearance: the class on `<html>`, and the storage the inline script in
 * `index.html` consults before first paint.
 *
 * Storage is tolerated failing outright, exactly as `language-memory` tolerates it: private mode
 * and blocked site data make the accessor itself throw, and a visitor who cannot be remembered is
 * not an error — they get the light site again on the next visit and press the button again.
 */
export function rememberTheme(theme: Theme): void {
    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        // Nothing to do and nothing to report.
    }
}

/** The remembered choice, resolved. `main.tsx` applies it; the inline script races it to the paint. */
export function rememberedTheme(): Theme {
    try {
        return preferredTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
        return preferredTheme(null);
    }
}

/**
 * What the visitor is looking at, read from the class rather than from storage.
 *
 * The class is what the page is painted by — written before first paint by the inline script, or by
 * `applyTheme` when the button was pressed — so a switch seeded from it cannot open describing an
 * appearance the visitor is not in, whatever storage did or did not answer.
 */
export function currentTheme(): Theme {
    return document.documentElement.classList.contains(DARK_CLASS) ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
    document.documentElement.classList.toggle(DARK_CLASS, theme === 'dark');
}
